import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Select, Modal, Table, Input, Button, Form } from 'antd';
import { ArrowLeft } from 'lucide-react';
import Swal from 'sweetalert2';
import moment from 'moment';
import {
  getAllAppraisalsByUserId,
  updateAppraisalAchievements,
  searchAppraisals,
} from '../../service/appraisalService';

const MyAppraisals = () => {
  const [appraisals, setAppraisals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedAppraisal, setSelectedAppraisal] = useState(null);
  const [openUpdateModal, setOpenUpdateModal] = useState(false);
  const [appraisalName, setAppraisalName] = useState('');
  const [periodName, setPeriodName] = useState('');
  const [departmentName, setDepartmentName] = useState('');
  const [status, setStatus] = useState('');
  const [pagination, setPagination] = useState({ current: 1, pageSize: 5, total: 0 });
  const [pageSizeModalVisible, setPageSizeModalVisible] = useState(false);
  const [tempPageSize, setTempPageSize] = useState(pagination.pageSize);
  const [form] = Form.useForm();
  const pageSizeOptions = [5, 10, 20, 50, 100];

  const getUserId = () => {
    try {
      const storedUserData = localStorage.getItem('user');
      if (!storedUserData) {
        console.error('User data not found in local storage.');
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'User data not found. Please log in again.',
        });
        return null;
      }
      const user = JSON.parse(storedUserData);
      return user.data._id; 
    } catch (error) {
      console.error('Error parsing user data:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to retrieve user data.',
      });
      return null;
    }
  };

  const userId = getUserId();

  useEffect(() => {
    if (userId) {
      fetchAppraisals();
    }
  }, [userId]);

  const fetchAppraisals = async () => {
    setLoading(true);
    const data = await getAllAppraisalsByUserId(userId);
    setLoading(false);
    if (data) {
      setAppraisals(data);
      setPagination((prev) => ({ ...prev, total: data.length }));
    } else {
      setAppraisals([]);
      setPagination((prev) => ({ ...prev, total: 0 }));
    }
  };

  const handleSearch = async () => {
    if (!userId) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'User not logged in.',
      });
      return;
    }
    const records = await searchAppraisals({
      userId,
      appraisalName,
      periodName,
      departmentName,
      status,
      setLoading,
    });
    if (records) {
      setAppraisals(records);
      setPagination((prev) => ({ ...prev, total: records.length, current: 1 }));
    } else {
      setAppraisals([]);
      setPagination((prev) => ({ ...prev, total: 0, current: 1 }));
    }
  };

  const handleTableChange = (pagination) => {
    setPagination(pagination);
  };

  const handlePageSizeChange = () => {
    const newPagination = { ...pagination, pageSize: tempPageSize, current: 1 };
    setPagination(newPagination);
    setPageSizeModalVisible(false);
  };

  const handleUpdateAchievements = async (values) => {
    try {
      const kpis = selectedAppraisal.kpis.map((kpi, index) => ({
        title: kpi.title,
        target: kpi.target,
        achieved: Number(values[`achieved_${index}`]) || 0,
        weightage: kpi.weightage,
      }));
      const updatedAppraisal = await updateAppraisalAchievements(
        userId,
        selectedAppraisal._id,
        kpis
      );
      setAppraisals((prev) =>
        prev.map((appraisal) =>
          appraisal._id === updatedAppraisal._id ? updatedAppraisal : appraisal
        )
      );
      setOpenUpdateModal(false);
      form.resetFields();
      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: 'Achievements updated successfully.',
      });
    } catch (error) {
      console.error('Error updating achievements:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.message || 'Failed to update achievements.',
      });
    }
  };

  const columns = [
    {
      title: 'Appraisal Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Period',
      dataIndex: 'period_id',
      key: 'period',
      render: (period) => period?.name || 'N/A',
    },
    {
      title: 'Department',
      dataIndex: 'department_id',
      key: 'department',
      render: (department) => department?.departmentName || 'N/A',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
    },
    {
      title: 'Final Score',
      dataIndex: 'final_score',
      key: 'final_score',
      render: (score) => score || 'N/A',
    },
    {
      title: 'Created Date',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date) => moment(date).format('YYYY-MM-DD'),
    },
    {
      title: 'Action',
      key: 'action',
      render: (record) => (
        <div className="action-table-data">
          <Link
            className="me-2 p-2"
            to="#"
            onClick={() => {
              setSelectedAppraisal(record);
              setOpenUpdateModal(true);
              form.setFieldsValue(
                record.kpis.reduce((acc, kpi, index) => ({
                  ...acc,
                  [`achieved_${index}`]: kpi.achieved,
                }), {})
              );
            }}
            title="Update achievements"
          >
            <i data-feather="edit" className="feather-edit"></i>
          </Link>
        </div>
      ),
    },
  ];

  if (!userId) {
    return (
      <div className="page-wrapper-lt">
        <div className="content">
          <div className="page-header">
            <div className="add-item d-flex">
              <div className="header-leave-container">
                <div>
                  <ArrowLeft size={32} />
                </div>
                <div className="page-title">
                  <h4>My Appraisals</h4>
                  <h6>Please log in to view your appraisals</h6>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-wrapper-lt">
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="header-leave-container">
              <div>
                <ArrowLeft size={32} />
              </div>
              <div className="page-title">
                <h4>My Appraisals</h4>
                <h6>Manage your performance appraisals</h6>
              </div>
            </div>
          </div>
        </div>
        <div className="card table-list-card-grid">
          <div className="card-body" style={{ maxWidth: '100%', marginRight: '20px' }}>
            <div
              className="container mt-3"
              style={{ maxWidth: '100%', marginLeft: '20px', marginRight: '40px' }}
            >
              <div className="row d-flex flex-wrap align-items-start justify-content-between">
                <div className="col-12 col-md-3 text-start">
                  <label className="form-label">Appraisal Name</label>
                  <Input
                    value={appraisalName}
                    onChange={(e) => setAppraisalName(e.target.value)}
                    placeholder="Enter appraisal name"
                    style={{ width: '80%', height: '40px' }}
                  />
                </div>
                <div className="col-12 col-md-3 text-start">
                  <label className="form-label">Period Name</label>
                  <Input
                    value={periodName}
                    onChange={(e) => setPeriodName(e.target.value)}
                    placeholder="Enter period name"
                    style={{ width: '80%', height: '40px' }}
                  />
                </div>
                <div className="col-12 col-md-3 mb-3 text-start">
                  <label className="form-label">Department</label>
                  <Input
                    value={departmentName}
                    onChange={(e) => setDepartmentName(e.target.value)}
                    placeholder="Enter department name"
                    style={{ width: '80%', height: '40px' }}
                  />
                </div>
                <div className="col-12 col-md-3 mb-3 text-start">
                  <label className="form-label">Status</label>
                  <Select
                    className="select"
                    options={[
                      { value: 'Draft', label: 'Draft' },
                      { value: 'InProgress', label: 'In Progress' },
                      { value: 'UnderReview', label: 'Under Review' },
                      { value: 'Completed', label: 'Completed' },
                    ]}
                    placeholder="Select status"
                    value={status}
                    onChange={(value) => setStatus(value)}
                    allowClear
                    showSearch
                    style={{ width: '80%', height: '40px' }}
                  />
                </div>
              </div>
            </div>
            <div
              className="container mt-0"
              style={{ maxWidth: '100%', marginLeft: '20px', marginRight: '40px' }}
            >
              <div className="row d-flex align-items-center justify-content-start">
                <div className="col-12 col-md-2 mb-3 text-start">
                  <button
                    disabled={loading}
                    className="btn btn-submit"
                    onClick={handleSearch}
                    style={{ height: '38px', width: '55%' }}
                  >
                    {loading ? 'Searching...' : 'Search'}
                  </button>
                  {loading && (
                    <div className="spinner">
                      <div className="loading-circle"></div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="card table-list-card-grid">
          <div className="card-body">
            <div className="table-top"></div>
            <div className="table-responsive">
              <Table
                loading={loading}
                columns={columns}
                dataSource={appraisals}
                rowKey="_id"
                pagination={{
                  current: pagination.current,
                  pageSize: pagination.pageSize,
                  pageSizeOptions: ['5', '10', '20', '50'],
                  total: pagination.total,
                  showSizeChanger: true,
                  showQuickJumper: true,
                  showTotal: (total) => `Total ${total} items`,
                }}
                onChange={handleTableChange}
              />
            </div>
          </div>
        </div>
      </div>

      <Modal
        zIndex={200000}
        open={openUpdateModal}
        onCancel={() => {
          setSelectedAppraisal(null);
          setOpenUpdateModal(false);
          form.resetFields();
        }}
        footer={null}
        maskClosable={false}
        closable={false}
        title="Update Achievements"
      >
        <Form form={form} onFinish={handleUpdateAchievements} layout="vertical">
          {selectedAppraisal?.kpis.map((kpi, index) => (
            <Form.Item
              key={index}
              label={`KPI: ${kpi.title} (Target: ${kpi.target}, Weightage: ${kpi.weightage}%)`}
              name={`achieved_${index}`}
              rules={[{ required: true, message: 'Please enter achieved value' }]}
            >
              <Input type="number" min={0} placeholder="Enter achieved value" />
            </Form.Item>
          ))}
          <Form.Item>
            <Button type="primary" htmlType="submit">
              Update
            </Button>
            <Button
              style={{ marginLeft: '10px' }}
              onClick={() => {
                setSelectedAppraisal(null);
                setOpenUpdateModal(false);
                form.resetFields();
              }}
            >
              Cancel
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Change Page Size"
        open={pageSizeModalVisible}
        onOk={handlePageSizeChange}
        onCancel={() => setPageSizeModalVisible(false)}
        footer={[
          <button
            key="cancel"
            className="btn btn-cancel"
            onClick={() => setPageSizeModalVisible(false)}
          >
            Cancel
          </button>,
          <button key="submit" className="btn btn-submit" onClick={handlePageSizeChange}>
            Apply
          </button>,
        ]}
      >
        <div className="form-group">
          <label>Items per page:</label>
          <Select
            style={{ width: '100%' }}
            value={tempPageSize}
            onChange={(value) => setTempPageSize(value)}
          >
            {pageSizeOptions.map((size) => (
              <Select.Option key={size} value={size}>
                {size}
              </Select.Option>
            ))}
          </Select>
        </div>
      </Modal>
    </div>
  );
};

export default MyAppraisals;